import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { PxWindow } from './PixelUI';
import { getRentals, stopRental, getWalletChallenge, verifyWallet } from '../api';

const ETHERSCAN_TX = 'https://etherscan.io/tx/';

const utf8Hex = (text) =>
  '0x' + Array.from(new TextEncoder().encode(text)).map((b) => b.toString(16).padStart(2, '0')).join('');

const TONE = {
  PROVISIONING: 'var(--nv-green)',
  PAID_QUEUED: 'var(--nv-gold)',
  PAYMENT_PENDING: '#8a6a10',
  EXPIRED: 'var(--nv-brown)',
  PAYMENT_UNVERIFIED: '#ff6b6b',
  PAID_PROVISION_FAILED: '#ff6b6b',
  REFUNDED: 'var(--nv-green)',
};

const STATUS_LABEL = { PAYMENT_PENDING: 'awaiting confirmation', REFUNDED: 'refunded' };

const shortHash = (h) => (h ? `${h.slice(0, 8)}…${h.slice(-6)}` : '—');
const shortDate = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const timeLeft = (secs) => {
  if (secs == null) return null;
  if (secs <= 0) return 'time up';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
};

const RentalRow = ({ r, onStop, stopping }) => {
  const tone = TONE[r.status] || 'var(--nv-cream)';
  const active = ['PROVISIONING', 'PAID_QUEUED'].includes(r.status);
  return (
    <div className="nv-rental-row" data-testid={`rental-row-${r.order_id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <span className="font-pixel" style={{ fontSize: 10, color: 'var(--nv-ink)' }}>{r.die_name}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: tone, textTransform: 'uppercase', letterSpacing: 1 }} data-testid={`rental-status-${r.order_id}`}>
          {STATUS_LABEL[r.status] || r.status}
        </span>
      </div>
      <div className="nv-kv"><span className="k">Rented</span><span className="v">{r.hours}h · {r.total_eth} ETH</span></div>
      <div className="nv-kv"><span className="k">When</span><span className="v" style={{ fontSize: 12 }}>{shortDate(r.created_at)}</span></div>
      {active && r.seconds_left != null && (
        <div className="nv-kv">
          <span className="k">Runs</span>
          <span className="v" style={{ fontSize: 12, color: r.expired ? 'var(--nv-gold)' : 'var(--nv-green)' }} data-testid={`rental-timeleft-${r.order_id}`}>
            {timeLeft(r.seconds_left)}
          </span>
        </div>
      )}
      <div className="nv-kv">
        <span className="k">Tx</span>
        <span className="v" style={{ fontSize: 12 }}>
          {r.tx_hash
            ? <a href={`${ETHERSCAN_TX}${r.tx_hash}`} target="_blank" rel="noreferrer" style={{ color: 'var(--nv-green)' }}>{shortHash(r.tx_hash)} ↗</a>
            : '—'}
        </span>
      </div>
      {r.connect && active && (
        <div className="nv-kv"><span className="k">Connect</span><span className="v" style={{ fontSize: 12 }}>{r.connect}</span></div>
      )}
      {(r.pod_ports || []).filter((p) => p.type === 'http').map((p) => (
        <div className="nv-kv" key={`j-${p.public}`}><span className="k">Jupyter</span><span className="v" style={{ fontSize: 12 }} data-testid={`rental-jupyter-${r.order_id}`}>{p.ip}:{p.public}</span></div>
      ))}
      {(r.pod_ports || []).filter((p) => p.type === 'tcp').map((p) => (
        <div className="nv-kv" key={`s-${p.public}`}><span className="k">SSH</span><span className="v" style={{ fontSize: 12 }} data-testid={`rental-ssh-${r.order_id}`}>{p.ip}:{p.public}</span></div>
      ))}
      {r.status === 'REFUNDED' && (
        <div className="nv-kv">
          <span className="k">Refund</span>
          <span className="v" style={{ fontSize: 12 }}>
            {r.refund_tx
              ? <a href={`${ETHERSCAN_TX}${r.refund_tx}`} target="_blank" rel="noreferrer" style={{ color: 'var(--nv-green)' }}>{shortHash(r.refund_tx)} ↗</a>
              : 'ETH returned'}
          </span>
        </div>
      )}
      {active && (
        <button
          className="px-btn sm dark"
          style={{ marginTop: 10, width: '100%' }}
          disabled={stopping}
          onClick={() => onStop(r.order_id)}
          data-testid={`rental-stop-${r.order_id}`}
        >
          {stopping ? 'STOPPING…' : 'STOP EARLY'}
        </button>
      )}
    </div>
  );
};

const MyRentals = () => {
  const [wallet, setWallet] = useState(null);
  const [token, setToken] = useState(null);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stoppingId, setStoppingId] = useState(null);

  const load = useCallback(async (addr, tok) => {
    setLoading(true);
    const rows = await getRentals(addr, tok);
    setRentals(rows || []);
    setLoading(false);
  }, []);

  const connect = async () => {
    if (!window.ethereum) {
      toast.error('No wallet docked — install MetaMask (or any EIP-1193 wallet) and reload');
      return;
    }
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const address = accounts[0];
      const message = await getWalletChallenge(address);
      const signature = await window.ethereum.request({ method: 'personal_sign', params: [utf8Hex(message), address] });
      const tok = await verifyWallet(address, message, signature);
      setWallet(address);
      setToken(tok);
      await load(address, tok);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Wallet did not connect';
      toast.error(detail.slice(0, 100));
    }
  };

  const onStop = async (orderId) => {
    setStoppingId(orderId);
    const res = await stopRental(orderId, token);
    if (res) {
      toast.success('Die stopped — rental ended');
      await load(wallet, token);
    } else {
      toast.error('Could not stop the die');
    }
    setStoppingId(null);
  };

  // Poll while connected so a pod failure or refund surfaces the moment it happens.
  useEffect(() => {
    if (!wallet || !token) return undefined;
    const id = setInterval(() => load(wallet, token), 30000);
    return () => clearInterval(id);
  }, [wallet, token, load]);

  const failed = rentals.filter((r) => r.status === 'PAID_PROVISION_FAILED');
  const refunded = rentals.filter((r) => r.status === 'REFUNDED');

  return (
    <PxWindow title="MY.DIES" tape>
      <div style={{ padding: 18 }} data-testid="my-rentals-panel">
        {!wallet ? (
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 12.5, color: 'var(--nv-brown)', margin: '0 0 14px', lineHeight: 1.7 }}>
              Connect and sign with the wallet you rent with to see every die you've booked —
              past and still running. Free signature, no transaction.
            </p>
            <button className="px-btn" onClick={connect} data-testid="connect-rentals-btn">CONNECT WALLET</button>
          </div>
        ) : (
          <>
            <div className="nv-kv">
              <span className="k">Wallet</span>
              <span className="v" style={{ fontSize: 12 }} data-testid="rentals-wallet">{shortHash(wallet)}</span>
            </div>
            {loading && <p style={{ fontSize: 12, color: 'var(--nv-brown)', marginTop: 12 }}>reading the ledger…</p>}
            {failed.length > 0 && (
              <div data-testid="rentals-alert-failed" style={{ marginTop: 12, padding: '10px 12px', border: '2px solid #ff6b6b', background: 'rgba(255,107,107,.12)', color: '#a11', fontSize: 12, lineHeight: 1.6 }}>
                ⚠ {failed.length === 1 ? 'A die' : `${failed.length} dies`} failed to boot — no GPU time was used. The owner refunds these to your wallet; it lands here as REFUNDED.
              </div>
            )}
            {refunded.length > 0 && (
              <div data-testid="rentals-alert-refunded" style={{ marginTop: 12, padding: '10px 12px', border: '2px solid var(--nv-green)', background: 'rgba(45,140,90,.12)', color: 'var(--nv-green)', fontSize: 12, lineHeight: 1.6 }}>
                ✓ You've been refunded for {refunded.length === 1 ? 'a die that' : `${refunded.length} dies that`} couldn't boot — the ETH is back in your wallet.
              </div>
            )}
            {!loading && rentals.length === 0 && (
              <p style={{ fontSize: 12.5, color: 'var(--nv-brown)', marginTop: 12 }} data-testid="rentals-empty">
                no dies on this wallet yet — rent one above and it lands here
              </p>
            )}
            {!loading && rentals.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 12 }}>
                {rentals.map((r) => (
                  <RentalRow key={r.order_id} r={r} onStop={onStop} stopping={stoppingId === r.order_id} />
                ))}
              </div>
            )}
            <button className="px-btn sm ghost" style={{ marginTop: 14 }} onClick={() => load(wallet, token)} data-testid="refresh-rentals-btn">REFRESH</button>
          </>
        )}
      </div>
    </PxWindow>
  );
};

export default MyRentals;
